import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { LineChart, BarChart, PieChart } from 'react-native-chart-kit';

import { useTheme } from '../../theme/ThemeContext';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { Button } from '../common/Button';
import type { AppDispatch } from '../../store';
import {
  fetchStudyGroupAnalytics,
  selectStudyGroupAnalytics,
  selectStudyGroupLoading,
  selectStudyGroupError,
  type StudyGroupAnalytics as AnalyticsType,
} from '../../store/slices/studyGroupSlice';

interface StudyGroupAnalyticsProps {
  groupId: string;
  showExportOptions?: boolean;
  onExport?: (format: 'pdf' | 'csv') => void;
}

const screenWidth = Dimensions.get('window').width;

export const StudyGroupAnalytics: React.FC<StudyGroupAnalyticsProps> = ({
  groupId,
  showExportOptions = false,
  onExport,
}) => {
  const { theme } = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const analytics = useSelector(selectStudyGroupAnalytics);
  const loading = useSelector(selectStudyGroupLoading);
  const error = useSelector(selectStudyGroupError);

  const [selectedMetric, setSelectedMetric] = useState<
    'engagement' | 'learning' | 'collaboration' | 'retention'
  >('engagement');

  const groupAnalytics = analytics[groupId];

  useEffect(() => {
    dispatch(fetchStudyGroupAnalytics(groupId));
  }, [dispatch, groupId]);

  const getMetricColor = (value: number) => {
    if (value >= 0.8) return theme.colors.success;
    if (value >= 0.6) return theme.colors.warning;
    return theme.colors.error;
  };

  const formatPercentage = (value: number) => `${Math.round(value * 100)}%`;

  const getEngagementData = () => {
    if (!groupAnalytics) return null;

    return {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      datasets: [
        {
          data: [
            groupAnalytics.averageEngagement * 0.8,
            groupAnalytics.averageEngagement * 0.9,
            groupAnalytics.averageEngagement * 1.1,
            groupAnalytics.averageEngagement * 0.95,
            groupAnalytics.averageEngagement * 1.05,
            groupAnalytics.averageEngagement * 1.2,
            groupAnalytics.averageEngagement * 0.85,
          ].map(v => Math.round(v * 100)),
        },
      ],
    };
  };

  const getSkillDistributionData = () => {
    if (!groupAnalytics) return null;

    const distribution = groupAnalytics.memberMetrics.skillLevelDistribution;
    return Object.entries(distribution).map(([level, count]) => ({
      name: level.charAt(0).toUpperCase() + level.slice(1),
      population: count,
      color:
        level === 'beginner'
          ? '#FF6B6B'
          : level === 'intermediate'
            ? '#4ECDC4'
            : '#45B7D1',
      legendFontColor: theme.colors.textSecondary,
      legendFontSize: 12,
    }));
  };

  const getActivityCompletionData = () => {
    if (!groupAnalytics) return null;

    const { activityMetrics } = groupAnalytics;
    return {
      labels: ['Discussions', 'Practice', 'Challenges', 'Reviews', 'Projects'],
      datasets: [
        {
          data: [
            Math.round(activityMetrics.averageParticipation * 100),
            Math.round(activityMetrics.activitySuccessRate * 100),
            Math.round(
              (activityMetrics.completedActivities /
                activityMetrics.totalActivities) *
                100
            ),
            85, // Mock data for reviews
            75, // Mock data for projects
          ],
        },
      ],
    };
  };

  const renderOverviewCards = () => {
    if (!groupAnalytics) return null;

    const cards = [
      {
        title: 'Total Members',
        value: groupAnalytics.totalMembers.toString(),
        subtitle: `${groupAnalytics.activeMembers} active`,
        icon: 'people',
        color: theme.colors.primary,
      },
      {
        title: 'Engagement Rate',
        value: formatPercentage(groupAnalytics.averageEngagement),
        subtitle: 'Last 30 days',
        icon: 'trending-up',
        color: getMetricColor(groupAnalytics.averageEngagement),
      },
      {
        title: 'Completion Rate',
        value: formatPercentage(groupAnalytics.completionRate),
        subtitle: 'Activities completed',
        icon: 'checkmark-circle',
        color: getMetricColor(groupAnalytics.completionRate),
      },
      {
        title: 'Satisfaction',
        value: formatPercentage(groupAnalytics.satisfactionScore),
        subtitle: 'Member satisfaction',
        icon: 'happy',
        color: getMetricColor(groupAnalytics.satisfactionScore),
      },
    ];

    return (
      <View style={styles.overviewCards}>
        {cards.map((card, index) => (
          <View
            key={index}
            style={[
              styles.overviewCard,
              { backgroundColor: theme.colors.surface },
            ]}
          >
            <View style={styles.cardHeader}>
              <Ionicons name={card.icon as any} size={24} color={card.color} />
              <Text style={[styles.cardValue, { color: theme.colors.text }]}>
                {card.value}
              </Text>
            </View>
            <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
              {card.title}
            </Text>
            <Text
              style={[
                styles.cardSubtitle,
                { color: theme.colors.textSecondary },
              ]}
            >
              {card.subtitle}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  const renderEngagementChart = () => {
    const data = getEngagementData();
    if (!data) return null;

    return (
      <View
        style={[styles.chartCard, { backgroundColor: theme.colors.surface }]}
      >
        <Text style={[styles.chartTitle, { color: theme.colors.text }]}>
          Weekly Engagement Trends
        </Text>
        <LineChart
          data={data}
          width={screenWidth - 64}
          height={200}
          yAxisSuffix='%'
          chartConfig={{
            backgroundColor: theme.colors.surface,
            backgroundGradientFrom: theme.colors.surface,
            backgroundGradientTo: theme.colors.surface,
            decimalPlaces: 0,
            color: (opacity = 1) => `rgba(0, 122, 255, ${opacity})`,
            labelColor: (opacity = 1) => theme.colors.textSecondary,
            style: {
              borderRadius: 16,
            },
          }}
          style={styles.chart}
        />
      </View>
    );
  };

  const renderSkillDistribution = () => {
    const data = getSkillDistributionData();
    if (!data || data.length === 0) return null;

    return (
      <View
        style={[styles.chartCard, { backgroundColor: theme.colors.surface }]}
      >
        <Text style={[styles.chartTitle, { color: theme.colors.text }]}>
          Member Skill Distribution
        </Text>
        <PieChart
          data={data}
          width={screenWidth - 64}
          height={200}
          chartConfig={{
            color: (opacity = 1) => `rgba(0, 122, 255, ${opacity})`,
          }}
          accessor='population'
          backgroundColor='transparent'
          paddingLeft='15'
          style={styles.chart}
        />
      </View>
    );
  };

  const renderActivityMetrics = () => {
    const data = getActivityCompletionData();
    if (!data || !groupAnalytics) return null;

    return (
      <View
        style={[styles.chartCard, { backgroundColor: theme.colors.surface }]}
      >
        <Text style={[styles.chartTitle, { color: theme.colors.text }]}>
          Activity Performance
        </Text>
        <BarChart
          data={data}
          width={screenWidth - 64}
          height={200}
          yAxisLabel=''
          yAxisSuffix='%'
          chartConfig={{
            backgroundColor: theme.colors.surface,
            backgroundGradientFrom: theme.colors.surface,
            backgroundGradientTo: theme.colors.surface,
            decimalPlaces: 0,
            color: (opacity = 1) => `rgba(52, 199, 89, ${opacity})`,
            labelColor: (opacity = 1) => theme.colors.textSecondary,
            style: {
              borderRadius: 16,
            },
          }}
          style={styles.chart}
        />
        <View style={styles.activityStats}>
          <View style={styles.activityStat}>
            <Text
              style={[styles.statLabel, { color: theme.colors.textSecondary }]}
            >
              Total Activities
            </Text>
            <Text style={[styles.statValue, { color: theme.colors.text }]}>
              {groupAnalytics.activityMetrics.totalActivities}
            </Text>
          </View>
          <View style={styles.activityStat}>
            <Text
              style={[styles.statLabel, { color: theme.colors.textSecondary }]}
            >
              Avg Duration
            </Text>
            <Text style={[styles.statValue, { color: theme.colors.text }]}>
              {Math.round(
                groupAnalytics.activityMetrics.averageActivityDuration
              )}
              m
            </Text>
          </View>
          <View style={styles.activityStat}>
            <Text
              style={[styles.statLabel, { color: theme.colors.textSecondary }]}
            >
              Success Rate
            </Text>
            <Text style={[styles.statValue, { color: theme.colors.text }]}>
              {formatPercentage(
                groupAnalytics.activityMetrics.activitySuccessRate
              )}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderMemberMetrics = () => {
    if (!groupAnalytics) return null;

    const { memberMetrics } = groupAnalytics;

    return (
      <View
        style={[styles.chartCard, { backgroundColor: theme.colors.surface }]}
      >
        <Text style={[styles.chartTitle, { color: theme.colors.text }]}>
          Member Performance Metrics
        </Text>
        <View style={styles.memberMetricsGrid}>
          <View style={styles.metricItem}>
            <Text
              style={[
                styles.metricLabel,
                { color: theme.colors.textSecondary },
              ]}
            >
              Average Skill Level
            </Text>
            <Text style={[styles.metricValue, { color: theme.colors.text }]}>
              {formatPercentage(memberMetrics.averageSkillLevel)}
            </Text>
          </View>
          <View style={styles.metricItem}>
            <Text
              style={[
                styles.metricLabel,
                { color: theme.colors.textSecondary },
              ]}
            >
              Member Retention
            </Text>
            <Text
              style={[
                styles.metricValue,
                { color: getMetricColor(memberMetrics.memberRetention) },
              ]}
            >
              {formatPercentage(memberMetrics.memberRetention)}
            </Text>
          </View>
          <View style={styles.metricItem}>
            <Text
              style={[
                styles.metricLabel,
                { color: theme.colors.textSecondary },
              ]}
            >
              Collaboration Index
            </Text>
            <Text
              style={[
                styles.metricValue,
                { color: getMetricColor(groupAnalytics.collaborationIndex) },
              ]}
            >
              {formatPercentage(groupAnalytics.collaborationIndex)}
            </Text>
          </View>
          <View style={styles.metricItem}>
            <Text
              style={[
                styles.metricLabel,
                { color: theme.colors.textSecondary },
              ]}
            >
              Diversity Index
            </Text>
            <Text style={[styles.metricValue, { color: theme.colors.primary }]}>
              {formatPercentage(groupAnalytics.diversityIndex)}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderTimeMetrics = () => {
    if (!groupAnalytics) return null;

    const { timeMetrics } = groupAnalytics;

    return (
      <View
        style={[styles.chartCard, { backgroundColor: theme.colors.surface }]}
      >
        <Text style={[styles.chartTitle, { color: theme.colors.text }]}>
          Time & Schedule Analytics
        </Text>
        <View style={styles.timeMetricsGrid}>
          <View style={styles.timeMetricItem}>
            <Ionicons name='time' size={20} color={theme.colors.primary} />
            <View style={styles.timeMetricInfo}>
              <Text
                style={[
                  styles.timeMetricLabel,
                  { color: theme.colors.textSecondary },
                ]}
              >
                Avg Session Duration
              </Text>
              <Text
                style={[styles.timeMetricValue, { color: theme.colors.text }]}
              >
                {Math.round(timeMetrics.averageSessionDuration)}m
              </Text>
            </View>
          </View>
          <View style={styles.timeMetricItem}>
            <Ionicons name='calendar' size={20} color={theme.colors.primary} />
            <View style={styles.timeMetricInfo}>
              <Text
                style={[
                  styles.timeMetricLabel,
                  { color: theme.colors.textSecondary },
                ]}
              >
                Session Frequency
              </Text>
              <Text
                style={[styles.timeMetricValue, { color: theme.colors.text }]}
              >
                {timeMetrics.sessionFrequency.toFixed(1)}/week
              </Text>
            </View>
          </View>
          <View style={styles.timeMetricItem}>
            <Ionicons
              name='checkmark-circle'
              size={20}
              color={theme.colors.success}
            />
            <View style={styles.timeMetricInfo}>
              <Text
                style={[
                  styles.timeMetricLabel,
                  { color: theme.colors.textSecondary },
                ]}
              >
                Schedule Adherence
              </Text>
              <Text
                style={[
                  styles.timeMetricValue,
                  { color: getMetricColor(timeMetrics.scheduleAdherence) },
                ]}
              >
                {formatPercentage(timeMetrics.scheduleAdherence)}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.peakHours}>
          <Text
            style={[
              styles.peakHoursLabel,
              { color: theme.colors.textSecondary },
            ]}
          >
            Peak Activity Hours:
          </Text>
          <View style={styles.peakHoursTags}>
            {timeMetrics.peakActivityHours.map((hour, index) => (
              <View
                key={index}
                style={[
                  styles.peakHourTag,
                  { backgroundColor: theme.colors.primaryLight },
                ]}
              >
                <Text
                  style={[styles.peakHourText, { color: theme.colors.primary }]}
                >
                  {hour}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  };

  const renderInsights = () => {
    if (!groupAnalytics) return null;

    const insights = [
      {
        icon: 'trending-up',
        title: 'High Engagement',
        description: `Your group shows ${formatPercentage(groupAnalytics.averageEngagement)} engagement, which is above average.`,
        type: 'positive',
      },
      {
        icon: 'people',
        title: 'Strong Collaboration',
        description: `Collaboration index of ${formatPercentage(groupAnalytics.collaborationIndex)} indicates excellent teamwork.`,
        type: 'positive',
      },
      {
        icon: 'time',
        title: 'Consistent Schedule',
        description: `${formatPercentage(groupAnalytics.timeMetrics.scheduleAdherence)} schedule adherence shows good commitment.`,
        type:
          groupAnalytics.timeMetrics.scheduleAdherence >= 0.7
            ? 'positive'
            : 'warning',
      },
      {
        icon: 'school',
        title: 'Learning Effectiveness',
        description: `${formatPercentage(groupAnalytics.learningEffectiveness)} learning effectiveness with room for improvement.`,
        type:
          groupAnalytics.learningEffectiveness >= 0.7 ? 'positive' : 'warning',
      },
    ];

    return (
      <View
        style={[styles.chartCard, { backgroundColor: theme.colors.surface }]}
      >
        <Text style={[styles.chartTitle, { color: theme.colors.text }]}>
          AI-Generated Insights
        </Text>
        {insights.map((insight, index) => (
          <View key={index} style={styles.insightItem}>
            <Ionicons
              name={insight.icon as any}
              size={20}
              color={
                insight.type === 'positive'
                  ? theme.colors.success
                  : insight.type === 'warning'
                    ? theme.colors.warning
                    : theme.colors.error
              }
            />
            <View style={styles.insightContent}>
              <Text style={[styles.insightTitle, { color: theme.colors.text }]}>
                {insight.title}
              </Text>
              <Text
                style={[
                  styles.insightDescription,
                  { color: theme.colors.textSecondary },
                ]}
              >
                {insight.description}
              </Text>
            </View>
          </View>
        ))}
      </View>
    );
  };

  if (loading.analytics) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingSpinner />
        <Text
          style={[styles.loadingText, { color: theme.colors.textSecondary }]}
        >
          Analyzing group performance...
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error}
        onRetry={() => dispatch(fetchStudyGroupAnalytics(groupId))}
      />
    );
  }

  if (!groupAnalytics) {
    return (
      <View style={styles.emptyContainer}>
        <Ionicons
          name='analytics-outline'
          size={64}
          color={theme.colors.textSecondary}
        />
        <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
          No analytics data available
        </Text>
        <Text
          style={[styles.emptySubtext, { color: theme.colors.textSecondary }]}
        >
          Analytics will be generated as your group engages with activities
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
          Group Analytics
        </Text>
        {showExportOptions && (
          <View style={styles.exportButtons}>
            <Button
              title='Export PDF'
              onPress={() => onExport?.('pdf')}
              variant='outline'
              size='small'
              icon='document'
            />
            <Button
              title='Export CSV'
              onPress={() => onExport?.('csv')}
              variant='outline'
              size='small'
              icon='download'
              style={styles.exportButton}
            />
          </View>
        )}
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Overview Cards */}
        {renderOverviewCards()}

        {/* Engagement Chart */}
        {renderEngagementChart()}

        {/* Skill Distribution */}
        {renderSkillDistribution()}

        {/* Activity Metrics */}
        {renderActivityMetrics()}

        {/* Member Metrics */}
        {renderMemberMetrics()}

        {/* Time Metrics */}
        {renderTimeMetrics()}

        {/* AI Insights */}
        {renderInsights()}

        {/* Last Updated */}
        <View style={styles.lastUpdated}>
          <Text
            style={[
              styles.lastUpdatedText,
              { color: theme.colors.textSecondary },
            ]}
          >
            Last updated:{' '}
            {new Date(groupAnalytics.lastUpdated).toLocaleString()}
          </Text>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  exportButtons: {
    flexDirection: 'row',
  },
  exportButton: {
    marginLeft: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  content: {
    flex: 1,
  },
  overviewCards: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  overviewCard: {
    width: '48%',
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
    marginHorizontal: '1%',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  cardValue: {
    fontSize: 20,
    fontWeight: '700',
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  cardSubtitle: {
    fontSize: 12,
  },
  chartCard: {
    borderRadius: 12,
    padding: 16,
    margin: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 16,
  },
  chart: {
    marginVertical: 8,
    borderRadius: 16,
  },
  activityStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 16,
  },
  activityStat: {
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  statValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  memberMetricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  metricItem: {
    width: '48%',
    marginBottom: 16,
    marginHorizontal: '1%',
  },
  metricLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: '600',
  },
  timeMetricsGrid: {
    marginBottom: 16,
  },
  timeMetricItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  timeMetricInfo: {
    marginLeft: 12,
  },
  timeMetricLabel: {
    fontSize: 12,
    marginBottom: 2,
  },
  timeMetricValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  peakHours: {
    marginTop: 16,
  },
  peakHoursLabel: {
    fontSize: 14,
    marginBottom: 8,
  },
  peakHoursTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  peakHourTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  peakHourText: {
    fontSize: 12,
    fontWeight: '500',
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  insightContent: {
    flex: 1,
    marginLeft: 12,
  },
  insightTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  insightDescription: {
    fontSize: 12,
    lineHeight: 16,
  },
  lastUpdated: {
    padding: 16,
    alignItems: 'center',
  },
  lastUpdatedText: {
    fontSize: 12,
    fontStyle: 'italic',
  },
});
